import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon, 
  ChevronUpIcon, 
  ChevronDownIcon,
  InformationCircleIcon,
  LockClosedIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest, buildApiUrl, API_CONFIG } from '../../utils/api';
import toast from 'react-hot-toast';

const Checkout = () => {
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'paypal'
  const [cardNumber, setCardNumber] = useState('');
  const [cardHolderFirstName, setCardHolderFirstName] = useState('');
  const [cardHolderLastName, setCardHolderLastName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [showPaymentDetails, setShowPaymentDetails] = useState(true);
  const [showBillingDetails, setShowBillingDetails] = useState(false);
  const [hasCardError, setHasCardError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('MXN');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const currencyOptions = [
    { code: 'USD', label: 'Dólar estadounidense (USD)', symbol: '$', rate: 0.058 },
    { code: 'MXN', label: 'Peso mexicano (MXN)', symbol: 'MX$', rate: 1 },
    { code: 'PEN', label: 'Sol peruano (PEN)', symbol: 'S/ ', rate: 0.21 },
    { code: 'COP', label: 'Peso colombiano (COP)', symbol: '$', rate: 252.5 },
    { code: 'USD-EC', label: 'Dólar estadounidense - Ecuador (USD)', symbol: '$', rate: 0.058 },
  ];

  const PLAN_IDS = {
    trial: 1,
    free: 2,
    basic: 3,
    standard: 4,
    pro: 5,
    enterprise: 6
  };

  const getSelectedCurrency = () => {
    return currencyOptions.find((option) => option.code === currency) || currencyOptions[1];
  };

  const formatCurrency = (amountInMXN) => {
    const selectedCurrency = getSelectedCurrency();
    const converted = Math.round(amountInMXN * selectedCurrency.rate);
    return {
      value: converted,
      code: selectedCurrency.code.includes('USD') ? 'USD' : selectedCurrency.code,
      symbol: selectedCurrency.symbol
    };
  };
 
  const currencyPrefixedCode = (code) => {
    if (code === 'USD-EC') {
      return 'USD';
    }
    return code;
  };

  const normalizePlanName = (name = '') =>
    name
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const getPlanIdFromName = (name = '') => {
    const normalized = normalizePlanName(name);
    if (normalized.includes('trial')) return PLAN_IDS.trial;
    if (normalized.includes('free') || normalized.includes('gratis')) return PLAN_IDS.free;
    if (normalized.includes('basic') || normalized.includes('basico')) return PLAN_IDS.basic;
    if (normalized.includes('standard') || normalized.includes('estandar')) return PLAN_IDS.standard;
    if (normalized.includes('pro')) return PLAN_IDS.pro;
    if (normalized.includes('enterprise') || normalized.includes('enterprice')) return PLAN_IDS.enterprise;
    return PLAN_IDS.basic;
  };

  const transformExpiryDate = (value = '') => {
    if (!value) return '';
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length < 4) return cleaned;
    const month = cleaned.slice(0, 2);
    const year = cleaned.slice(2);
    const fullYear = year.length === 2 ? `20${year}` : year.padStart(4, '0');
    return `${month}01${fullYear}`;
  };

  // Obtener los datos del plan desde el estado de navegación o localStorage
  const getPlanData = () => {
    if (location.state) {
      return location.state;
    }
    // Fallback: datos por defecto si no hay state
    const savedData = localStorage.getItem('selectedPlan');
    if (savedData) {
      return JSON.parse(savedData);
    }
    // Plan por defecto
    return {
      planName: 'Básico',
      numUsers: 5,
      billingCycle: 'annual',
      pricePerUser: 190,
      total: 950,
      originalPrice: 1400,
      savings: 450,
      monthlyPrice: 950
    };
  };

  const planData = getPlanData();

  // Calcular el total anual si es plan anual
  const calculateTotal = () => {
    if (planData.billingCycle === 'annual') {
      return planData.monthlyPrice * 12;
    }
    return planData.monthlyPrice;
  };

  const total = calculateTotal();
  const originalTotal = planData.originalPrice || total;
  const savings = originalTotal - total;

  const convertedTotal = useMemo(() => formatCurrency(total), [total, currency]);
  const convertedOriginalTotal = useMemo(() => formatCurrency(originalTotal), [originalTotal, currency]);
  const convertedMonthlyPrice = useMemo(() => formatCurrency(planData.monthlyPrice), [planData.monthlyPrice, currency]);
  const convertedSavings = useMemo(
    () => Math.max(convertedOriginalTotal.value - convertedTotal.value, 0),
    [convertedOriginalTotal.value, convertedTotal.value]
  );
  const selectedCurrency = useMemo(() => getSelectedCurrency(), [currency]);
  const modalCurrencySymbol = selectedCurrency.symbol;
  const modalCurrencyCode = currencyPrefixedCode(currency);

  // Formatear fecha del próximo cargo (12 meses desde ahora si es anual, 1 mes si es mensual)
  const getNextChargeDate = () => {
    const date = new Date();
    if (planData.billingCycle === 'annual') {
      date.setFullYear(date.getFullYear() + 1);
    } else {
      date.setMonth(date.getMonth() + 1);
    }
    return date.toLocaleDateString('es-MX', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleCardNumberChange = (e) => {
    let value = e.target.value.replace(/\s/g, '');
    if (value.length <= 16) {
      setCardNumber(value);
      // Validación básica: al menos 13 dígitos (Visa min), máximo 19
      if (value.length >= 13 && value.length <= 16) {
        setHasCardError(false);
      } else if (value.length > 0 && value.length < 13) {
        setHasCardError(true);
      } else {
        setHasCardError(false);
      }
    }
  };

  const formatCardNumber = (value) => {
    return value.replace(/(.{4})/g, '$1 ').trim();
  };

  const handleExpiryChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length <= 4) {
      setExpiryDate(value);
    }
  };

  const formatExpiryDate = (value) => {
    if (value.length >= 2) {
      return value.slice(0, 2) + ' / ' + value.slice(2);
    }
    return value;
  };

  const handleCvvChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length <= 4) {
      setCvv(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    const sanitizedPan = cardNumber.replace(/\s/g, '');

    if (!sanitizedPan || sanitizedPan.length !== 16) {
      toast.error('Por favor ingresa un número de tarjeta válido');
      setHasCardError(true);
      return;
    }
    
    if (!cardHolderFirstName || !cardHolderLastName) {
      toast.error('Por favor ingresa el nombre completo del titular');
      return;
    }
    
    if (!expiryDate || expiryDate.length !== 4) {
      toast.error('Por favor ingresa una fecha de vencimiento válida');
      return;
    }
    
    if (!cvv || cvv.length < 3) {
      toast.error('Por favor ingresa un código de seguridad válido');
      return;
    }

    const customerEmail = user?.email;

    if (!customerEmail) {
      toast.error('No encontramos un correo electrónico válido para tu cuenta. Actualiza tu perfil e intenta de nuevo.');
      return;
    }
 
    setLoading(true);

    const apiCurrency = currencyPrefixedCode(currency);
    const cycle = planData.billingCycle === 'annual' ? 'annual' : 'monthly';
    const planId = getPlanIdFromName(planData.planName);
    const payload = {
      name: cardHolderFirstName,
      lastName: cardHolderLastName,
      email: customerEmail,
      pan: sanitizedPan,
      ccv: cvv,
      amount: Math.max(convertedTotal.value, 1),
      expirationDate: transformExpiryDate(expiryDate),
      currency: apiCurrency,
      plan: planId,
      cycle,
      autoRenew: true,
      userLimit: Number(planData.numUsers) || 1
    };

    try {
      await apiRequest(buildApiUrl(API_CONFIG.ENDPOINTS.PAYMENTS), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      toast.success('Tu plan fue renovado exitosamente');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Hubo problemas al procesar tu pago. Por favor reintenta.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          {showSuccessModal && (
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="max-w-lg w-full bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-primary-200 dark:border-primary-800 p-8"
              >
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-primary-100 dark:bg-primary-900/40 rounded-full flex items-center justify-center">
                    <span className="text-3xl">🎉</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">¡Gracias por tu pago!</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Has hecho una excelente elección. Sigue gestionando a tu equipo de forma eficiente con todas las funcionalidades de TeamiFlow.
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-left">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Resumen de tu plan</h3>
                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                      <li className="flex justify-between">
                        <span>Plan seleccionado</span>
                        <span className="font-medium text-gray-900 dark:text-white">{planData.planName}</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Usuarios incluidos</span>
                        <span className="font-medium text-gray-900 dark:text-white">{planData.numUsers}</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Ciclo de facturación</span>
                        <span className="font-medium text-gray-900 dark:text-white">{planData.billingCycle === 'annual' ? 'Anual' : 'Mensual'}</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Total pagado</span>
                        <span className="font-medium text-primary-600 dark:text-primary-300">
                          {modalCurrencySymbol}
                          {convertedTotal.value.toLocaleString('es-MX')} {modalCurrencyCode}
                        </span>
                      </li>
                    </ul>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSuccessModal(false);
                      navigate('/dashboard');
                    }}
                    className="w-full btn-primary py-3 text-base font-semibold"
                  >
                    Continuar
                  </button>
                </div>
              </motion.div>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <Link
              to="/pricing"
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium inline-flex items-center"
            >
              ← Volver a los planes
            </Link>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {theme === 'light' ? '🌙' : '☀️'}
              </button>
              <Link
                to="/login"
                className="p-2 rounded-lg bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <XMarkIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </Link>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
            Agrega los detalles de pago y completa la compra
          </h1>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Section - Payment Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Seleccionar método de pago */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => setShowPaymentDetails(!showPaymentDetails)}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Seleccionar método de pago
                  </h2>
                  {showPaymentDetails ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>

                <AnimatePresence>
                  {showPaymentDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="p-6 space-y-6">
                        {/* Payment Method Options */}
                        <div className="flex flex-col space-y-4">
                          <div className="flex space-x-4">
                            <button
                              onClick={() => setPaymentMethod('card')}
                              className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                                paymentMethod === 'card'
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-primary-300'
                              }`}
                            >
                              <span className={`font-medium ${
                                paymentMethod === 'card'
                                  ? 'text-primary-600 dark:text-primary-400'
                                  : 'text-gray-700 dark:text-gray-300'
                              }`}>
                                Tarjeta de crédito
                              </span>
                            </button>
                            <button
                              onClick={() => setPaymentMethod('paypal')}
                              className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                                paymentMethod === 'paypal'
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-primary-300'
                              }`}
                            >
                              <span className={`font-medium ${
                                paymentMethod === 'paypal'
                                  ? 'text-primary-600 dark:text-primary-400'
                                  : 'text-gray-700 dark:text-gray-300'
                              }`}>
                                PayPal
                              </span>
                            </button>
                          </div>

                          {/* Currency Selector */}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Moneda de pago
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Convierte automáticamente los precios y redondea a enteros.
                              </p>
                            </div>
                            <select
                              value={currency}
                              onChange={(e) => setCurrency(e.target.value)}
                              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              {currencyOptions.map((option) => (
                                <option key={option.code} value={option.code}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Card Logos */}
                        {paymentMethod === 'card' && (
                          <div className="flex items-center justify-center space-x-2">
                            <div className="flex -space-x-2">
                              <div className="w-12 h-8 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">VISA</div>
                              <div className="w-12 h-8 bg-orange-500 rounded text-white text-xs flex items-center justify-center font-bold">MC</div>
                              <div className="w-12 h-8 bg-blue-700 rounded text-white text-xs flex items-center justify-center font-bold">AE</div>
                              <div className="w-12 h-8 bg-blue-800 rounded text-white text-xs flex items-center justify-center font-bold">DC</div>
                              <div className="w-12 h-8 bg-orange-600 rounded text-white text-xs flex items-center justify-center font-bold">D</div>
                              <div className="w-12 h-8 bg-green-700 rounded text-white text-xs flex items-center justify-center font-bold">JCB</div>
                            </div>
                          </div>
                        )}

                        {/* Card Input Fields */}
                        {paymentMethod === 'card' && (
                          <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Card Number */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Número de tarjeta
                              </label>
                              <input
                                type="text"
                                value={formatCardNumber(cardNumber)}
                                onChange={handleCardNumberChange}
                                maxLength={19}
                                className={`w-full px-4 py-3 rounded-lg border-2 transition-colors ${
                                  hasCardError
                                    ? 'border-red-500 focus:border-red-500'
                                    : 'border-gray-300 dark:border-gray-600 focus:border-primary-500'
                                } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                                placeholder="0000 0000 0000 0000"
                              />
                              {hasCardError && (
                                <p className="mt-2 text-sm text-red-600">
                                  Verifica el número de tu tarjeta de crédito
                                </p>
                              )}
                            </div>

                            {/* Card Holder Name */}
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Nombre del titular de la tarjeta
                                </label>
                                <input
                                  type="text"
                                  value={cardHolderFirstName}
                                  onChange={(e) => setCardHolderFirstName(e.target.value)}
                                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  placeholder="Nombre"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Apellido del titular de la tarjeta
                                </label>
                                <input
                                  type="text"
                                  value={cardHolderLastName}
                                  onChange={(e) => setCardHolderLastName(e.target.value)}
                                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  placeholder="Apellido"
                                />
                              </div>
                            </div>

                            {/* Expiry and CVV */}
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Vencimiento
                                </label>
                                <input
                                  type="text"
                                  value={formatExpiryDate(expiryDate)}
                                  onChange={handleExpiryChange}
                                  maxLength={7}
                                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  placeholder="MM / YY"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center space-x-1">
                                  <span>Código de seguridad</span>
                                  <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                                </label>
                                <input
                                  type="text"
                                  value={cvv}
                                  onChange={handleCvvChange}
                                  maxLength={4}
                                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  placeholder="CVV"
                                />
                              </div>
                            </div>
                          </form>
                        )}

                        {/* PayPal Info */}
                        {paymentMethod === 'paypal' && (
                          <div className="text-center py-8">
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                              Serás redirigido a PayPal para completar tu pago
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Detalles de factura */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => setShowBillingDetails(!showBillingDetails)}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Detalles de factura
                  </h2>
                  {showBillingDetails ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>

                <AnimatePresence>
                  {showBillingDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="p-6">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Los detalles de facturación serán los mismos que los de la tarjeta
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Privacy Statement */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-900 dark:text-blue-200">
                  Tu privacidad es muy importante para nosotros. Solo usaremos tu información según lo descrito en nuestros{' '}
                  <button className="underline font-medium">términos de uso</button>
                  {' '}y{' '}
                  <button className="underline font-medium">Política de privacidad</button>.
                </p>
              </div>
            </div>

            {/* Right Section - Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6 sticky top-6">
                {/* Refund Guarantee */}
                <div className="bg-teal-900 dark:bg-teal-950 rounded-lg p-4 mb-6 flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white">
                      Garantía de reembolso
                    </p>
                    <p className="text-xs text-teal-200 mt-1">
                      Reembolso prorrateado dentro de...
                    </p>
                  </div>
                  <div className="relative">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                      <span className="text-2xl">👍</span>
                    </div>
                    <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1">
                      <CheckCircleIcon className="h-4 w-4 text-white" />
                    </div>
                  </div>
                </div>

                {/* Savings */}
                {savings > 0 && (
                  <div className="mb-6">
                    <p className="text-sm font-medium text-success-600">
                      Ahorraste {convertedOriginalTotal.symbol}
                      {convertedSavings.toLocaleString('es-MX')} {currencyPrefixedCode(currency)} con el plan {planData.billingCycle === 'annual' ? 'Anual' : 'Mensual'}
                    </p>
                  </div>
                )}

                {/* Product Name */}
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
                    work management
                  </p>
                </div>

                {/* Plan Details */}
                <div className="mb-6 space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        Plan {planData.planName} {planData.numUsers} usuarios
                      </p>
                      {planData.billingCycle === 'annual' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          ({convertedMonthlyPrice.symbol}
                          {convertedMonthlyPrice.value.toLocaleString('es-MX')} {currencyPrefixedCode(currency)} x 12 meses)
                        </p>
                      )}
                    </div>
                    {savings > 0 && (
                      <div className="text-right">
                        <p className="text-sm line-through text-gray-400">
                          {convertedOriginalTotal.symbol}
                          {convertedOriginalTotal.value.toLocaleString('es-MX')} {currencyPrefixedCode(currency)}
                        </p>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {convertedTotal.symbol}
                          {convertedTotal.value.toLocaleString('es-MX')} {currencyPrefixedCode(currency)}
                        </p>
                      </div>
                    )}
                    {savings === 0 && (
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {convertedTotal.symbol}
                          {convertedTotal.value.toLocaleString('es-MX')} {currencyPrefixedCode(currency)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Total */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-base font-medium text-gray-900 dark:text-white">
                      Total
                    </span>
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">
                      {convertedTotal.symbol}
                      {convertedTotal.value.toLocaleString('es-MX')} {currencyPrefixedCode(currency)}
                    </span>
                  </div>
                </div>

                {/* Next Charge Date */}
                <div className="mb-6">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Fecha del próximo cargo: {getNextChargeDate()}
                  </p>
                </div>

                {/* Complete Purchase Button */}
                <button
                  onClick={paymentMethod === 'card' ? handleSubmit : () => toast.info('Integración de PayPal en desarrollo')}
                  disabled={loading}
                  className="w-full btn-primary py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Procesando...</span>
                    </>
                  ) : (
                    <span>Completa tu compra</span>
                  )}
                </button>

                {/* Secure Payment */}
                <div className="mt-4 flex items-center justify-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                  <LockClosedIcon className="h-4 w-4" />
                  <span>Pago seguro</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Section */}
          <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left - Refund Guarantee Details */}
              <div className="flex items-start space-x-3">
                <CheckCircleIcon className="h-6 w-6 text-success-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white mb-1">
                    Garantía de reembolso
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Puedes darte de baja fácilmente dentro de los primeros 30 días y obtener un reembolso prorrateado. No hay preguntas.{' '}
                    <InformationCircleIcon className="h-4 w-4 inline-block text-gray-400 ml-1" />
                  </p>
                </div>
              </div>

              {/* Right - Security Badges */}
              <div className="flex items-center justify-end flex-wrap gap-3">
                <div className="text-xs font-semibold text-blue-600 dark:text-blue-400">Verified by Visa SecureCode</div>
                <div className="text-xs font-semibold text-orange-600 dark:text-orange-400">MasterCard SecureCode</div>
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Norton Secured</div>
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">McAfee Secure</div>
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">SSL ENCRYPTION</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Need Help Button */}
      <div className="fixed bottom-6 right-6">
        <button className="bg-white dark:bg-gray-800 rounded-full px-6 py-3 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            ¿Necesitas ayuda?
          </span>
        </button>
      </div>
    </div>
  );
};

export default Checkout;

